import { useEffect, useState } from "react";
import { Layout } from "./components/layout/Layout";
import { StatsCard } from "./components/StatsCard";
import { supabaseReservation } from "../../../integrations/supabase/client";
import { Phone, PhoneCall, Clock } from "lucide-react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";

interface Conversation {
  id: string;
  customer_name: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  cost_cents?: number | null;
}

const Conversations = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [costMap, setCostMap] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchConversations();

    // Set up realtime subscription
    const channel = supabaseReservation
      .channel('conversations-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabaseReservation.removeChannel(channel);
    };
  }, []);

  const fetchConversations = async () => {
    // Fetch conversations
    const { data, error } = await supabaseReservation
      .from('conversations')
      .select('*')
      .order('started_at', { ascending: false });

    if (error) {
      console.error('Error fetching conversations:', error);
    } else {
      setConversations(data || []);
      
      // Fetch costs from credit_transactions for each conversation
      if (data && data.length > 0) {
        const conversationIds = data.map(c => c.id);
        const { data: transactions } = await supabaseReservation
          .from('credit_transactions')
          .select('conversation_id, amount_cents')
          .in('conversation_id', conversationIds)
          .eq('type', 'usage');
        
        if (transactions) {
          const costs: Record<string, number> = {};
          transactions.forEach(tx => {
            if (tx.conversation_id) {
              // amount_cents is negative for usage, so we use Math.abs
              costs[tx.conversation_id] = Math.abs(tx.amount_cents);
            }
          });
          setCostMap(costs);
        }
      }
    }
    setLoading(false);
  };

  const totalCalls = conversations.length;
  const activeCalls = conversations.filter(c => c.status === 'active').length;
  const avgDuration = conversations.length > 0
    ? Math.round(
        conversations
          .filter(c => c.duration_seconds)
          .reduce((sum, c) => sum + (c.duration_seconds || 0), 0) /
        (conversations.filter(c => c.duration_seconds).length || 1)
      )
    : 0;

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Conversations</h1>
          <p className="text-muted-foreground mt-1">Monitor all voice interactions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatsCard
            title="Total Calls"
            value={totalCalls}
            icon={Phone}
          />
          <StatsCard
            title="Active Now"
            value={activeCalls}
            icon={PhoneCall}
          />
          <StatsCard
            title="Avg Duration"
            value={`${formatDuration(avgDuration)}`}
            icon={Clock}
          />
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Customer Name</TableHead>
                <TableHead className="text-muted-foreground">Date</TableHead>
                <TableHead className="text-muted-foreground">Duration</TableHead>
                <TableHead className="text-muted-foreground">Cost</TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : conversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No conversations yet
                  </TableCell>
                </TableRow>
              ) : (
                conversations.map((conversation) => (
                  <TableRow key={conversation.id} className="border-border">
                    <TableCell className="font-medium text-card-foreground">
                      {conversation.customer_name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(conversation.started_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDuration(conversation.duration_seconds)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {costMap[conversation.id] !== undefined
                        ? `$${(costMap[conversation.id] / 100).toFixed(2)}`
                        : conversation.status === 'active' 
                          ? '-' 
                          : '$0.00'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={conversation.status === 'active' ? 'default' : 'secondary'}
                        className={
                          conversation.status === 'active'
                            ? 'bg-primary text-primary-foreground'
                            : ''
                        }
                      >
                        {conversation.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};

export default Conversations;
